'use client';
import Image from 'next/image';
import Link from 'next/link';
import { Suspense } from 'react';
import MediaModal from '@/components/ui/media-modal';
import { PortableTextWrapper } from '@/components/PortableTextWrapper';
import { PortableText } from '@portabletext/react';
import type { ArbitraryTypedObject } from '@portabletext/types';
import { PortableTextBlock } from '@sanity/types';
import { getSanityImageUrl } from '@/libs/sanity/client';
import { SlArrowRightCircle } from 'react-icons/sl';
import { BiExpandAlt } from 'react-icons/bi';
import { useState } from 'react';
import VideoModal from '@/components/VideoModal';
import { BlurInH2 } from '@/components/eldora-ui/blur-in-h2';
import KeyFeatures from './KeyFeatures';
import { KeyFeature } from './KeyFeatures';

export type Video = {
  buttonLabel?: string;
  buttonLongLabel?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  coverImage: any;
  videoUrl: string;
  description?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  outsideDescription?:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  title?: string;
  link?: any;
  learnMore?: any;
};

type LeftTextRightImageProps = {
  anchor?: string;
  title?: string;
  image: any;
  leftContent:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
  readMoreLink?: any;
  video?: Video;
  autoPlayVideo?: Video;
  keyFeatures?: KeyFeature[];
  keyFeaturesTitle?: string;
};

const LeftTextRightImageAndVideo = ({
  anchor,
  image,
  title,
  leftContent,
  readMoreLink,
  video,
  autoPlayVideo,
  keyFeatures,
  keyFeaturesTitle,
}: LeftTextRightImageProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const renderVideoButton = (video: Video) => {
    const isDefaultLabel =
      !video.buttonLabel || video.buttonLabel === 'Enlarge Video';

    return (
      <div
        className={`mt-4 justify-start text-primary gap-x-1 items-center cursor-pointer ${isDefaultLabel ? 'flex flex-row' : ''}`}
        onClick={() => setIsModalOpen(true)}
      >
        {video.buttonLabel && (
          <>
            {isDefaultLabel && <BiExpandAlt className="font-bold" />}
            <span>{video.buttonLabel || 'Enlarge Video'}</span>
            {!isDefaultLabel && (
              <SlArrowRightCircle
                className="text-primary mt-1 block"
                size={18}
              />
            )}
          </>
        )}
        {video.buttonLongLabel && (
          <>
            <div className="text-primary">
              <PortableText value={video.buttonLongLabel} />
              <SlArrowRightCircle
                className="text-primary mt-1 block"
                size={18}
              />
            </div>
          </>
        )}
      </div>
    );
  };
  return (
    <section id={anchor} className="max-w-8xl mx-auto mt-12 scroll-mt-[150px]">
      <div className="flex flex-col lg:flex-row justify-center gap-x-8 flex-wrap">
        <div className="w-full xl:w-1/2">
          <BlurInH2>{title}</BlurInH2>
          {leftContent && <PortableTextWrapper value={leftContent} />}
        </div>
        <div className="w-full xl:w-1/2 flex flex-1 flex-col justify-start gap-y-16 mb-5">
          {image && (
            <div className="mb-8">
              <div className="mb-2">
                <Image
                  src={getSanityImageUrl(image)}
                  alt="Image"
                  className="object-cover"
                  loading="lazy"
                  width={1000}
                  height={2000}
                />
              </div>
              {readMoreLink && readMoreLink.internal && (
                <Link
                  href={readMoreLink.internal.ref?.metadata?.slug?.current}
                  className="text-primary mt-4 text-md hover:underline"
                >
                  {readMoreLink.label}
                  <SlArrowRightCircle className="text-primary mt-1" size={18} />
                </Link>
              )}
            </div>
          )}
          {video && video.coverImage && (
            <div>
              <div className="mt-2">
                <Image
                  src={getSanityImageUrl(video.coverImage)}
                  alt="Image"
                  className="object-cover"
                  loading="lazy"
                  width={1000}
                  height={2000}
                />
              </div>
              {renderVideoButton(video)}
            </div>
          )}
          {autoPlayVideo && autoPlayVideo.videoUrl && (
            <div>
              <Suspense fallback={<p>Loading video...</p>}>
                <MediaModal
                  className=" object-cover"
                  videoSrc={autoPlayVideo.videoUrl}
                  outsideDescription={autoPlayVideo.outsideDescription}
                  description={autoPlayVideo.description}
                  title={autoPlayVideo.title}
                  buttonLabel={autoPlayVideo?.buttonLabel}
                ></MediaModal>
                {/*<video className="h-[155px] object-cover" muted autoPlay loop>*/}
                {/*  <source src={video.videoUrl} type="video/mp4" />*/}
                {/*  Your browser does not support the video tag.*/}
                {/*</video>*/}
              </Suspense>
            </div>
          )}
        </div>
      </div>
      {keyFeatures && (
        <KeyFeatures title={keyFeaturesTitle} keyFeatures={keyFeatures} />
      )}
      {video && (
        <VideoModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          video={video}
        />
      )}
    </section>
  );
};
export default LeftTextRightImageAndVideo;
